import React, { useContext, useEffect, useRef, useState } from 'react';
import Helmet from 'react-helmet';
import { useLocation, useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';
import FrameFacade from '~/pages/AppPage/components/FrameFacade';
import JustificationContainer from '~/components/JustificationContainer';
import HeightContext from '~/context/HeightContext';
import useDebouncedCallback from '~/hooks/useDebouncedCallback';
import useLocale from '~/hooks/useLocale';
import useWidgetContext from '~/hooks/useWidgetContext';
import { layoutSelector, widgetState } from '../../state/widgetState';
import useMessaging from '~/hooks/useMessaging';
import useTracking from '~/hooks/useTracking';

const AppPage: React.FC = () => {
  const location = useLocation();
  const locale = useLocale();
  const doTrack = useTracking();

  const { headerRef, footerRef } = useContext(HeightContext);
  const context = useWidgetContext();

  const [minHeight, setMinHeight] = useState<number>(300);
  const [state, setWidgetState] = useRecoilState(widgetState);
  const [layout, setLayout] = useRecoilState(layoutSelector);

  const mainRef = useRef<HTMLDivElement | null>(null);
  const { slug } = useParams();

  // Only set loading state on mount
  useEffect(() => {
    setWidgetState(prev => ({ ...prev, loading: true }));
  }, []);

  // Update title
  useEffect(() => {
    if (typeof slug === 'string' && context.apps[slug]) {
      doTrack({ name: 'app_shown', slug });

      setTimeout(() => {
        setWidgetState(prev => ({
          ...prev,
          title: context.apps[slug].title[locale],
        }));
      }, 0);
    }

    return () => {
      // Reset title when we navigate away
      setWidgetState(prev => ({
        ...prev,
        title: null,
      }));
    };
    // Update title when locale changes
  }, [locale, slug]);

  useMessaging(message => {
    switch (message.type) {
      case 'track': {
        const messageName = message.payload?.name ?? 'unknown';
        const params = message.payload?.params ?? {};
        const { conversion, featureFlags = {}, ...properties } = params;

        doTrack({
          name: 'app_event',
          slug: slug ?? 'unknown',
          is_conversion: conversion === true,
          messageName,
          properties,
          featureFlags,
        });
        break;
      }

      default:
        break;
    }
  });

  useEffect(() => {
    if (layout !== 'wide') {
      setLayout('wide');
    }
  }, [layout, location.pathname]);

  const onResize = useDebouncedCallback(() => {
    if (headerRef.current === null || footerRef.current === null) return;

    const headerBound = headerRef.current?.getBoundingClientRect();
    const footerBound = footerRef.current?.getBoundingClientRect();
    const minHeight = footerBound.y - (headerBound.y - headerBound.height);
    setMinHeight(() => minHeight - 5);
  }, 50);

  useEffect(() => {
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (typeof slug !== 'string') return <div>TODO: NOT FOUND</div>;
  const app = context.apps[slug];
  if (!app) return <div>TODO: NOT FOUND</div>;

  return (
    <Main ref={mainRef}>
      <Helmet>
        <title>{state.title}</title>
      </Helmet>
      <Container justification="start" align="center">
        <FrameContainer>
          <FrameFacade app={app} minHeight={minHeight} />
        </FrameContainer>
      </Container>
    </Main>
  );
};

const Container = styled(JustificationContainer)`
  height: 100%;
`;

const Main = styled.main`
  padding: 0;
  height: 100%;
`;

const FrameContainer = styled.div`
  width: 100%;
  height: 100%;
  min-width: 100%;
  margin: 0 auto;
`;

export default AppPage;
